import * as React from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { TrendingUp, TrendingDown } from "lucide-react"

export interface MetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  value: string | number
  description?: string
  trend?: {
    value: number
    label: string
  }
  sparkline?: React.ReactNode
  size?: "large" | "small"
  accentColor?: "primary" | "success" | "warning" | "danger" | "info"
  statusBandColor?: "success" | "warning" | "danger" | "info" // Status band color by thresholds
}

const statusBandColorMap = {
  success: "bg-admin-success",
  warning: "bg-admin-warning",
  danger: "bg-admin-danger",
  info: "bg-admin-info",
}

export function MetricCard({
  title,
  value,
  description,
  trend,
  sparkline,
  size = "large",
  accentColor,
  statusBandColor,
  className,
  ...props
}: MetricCardProps) {
  const isPositive = trend ? trend.value >= 0 : null
  
  return (
    <Card className={cn("relative overflow-hidden", className)} {...props}>
      {/* Status band (bottom border) */}
      {statusBandColor && (
        <div className={cn(
          "absolute bottom-0 left-0 right-0 h-1",
          statusBandColorMap[statusBandColor]
        )} />
      )}
      
      <CardHeader className="pb-2">
        <CardDescription className="text-admin-label">{title}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline justify-between">
          <div className="flex-1">
            <div className={cn(
              "font-bold text-foreground",
              size === "large" ? "text-admin-metric-lg" : "text-admin-metric-sm"
            )}>
              {value}
            </div>
            {description && (
              <p className="text-admin-data text-muted-foreground mt-1">{description}</p>
            )}
          </div>
          {sparkline && (
            <div className="ml-4 w-20 h-12 flex items-end">
              {sparkline}
            </div>
          )}
        </div>
        {trend && (
          <div className={cn(
            "flex items-center gap-1 mt-2 text-sm",
            isPositive ? "text-admin-success" : "text-admin-danger"
          )}>
            {isPositive ? (
              <TrendingUp className="h-4 w-4" />
            ) : (
              <TrendingDown className="h-4 w-4" />
            )}
            <span className="font-medium">{Math.abs(trend.value)}%</span>
            <span className="text-muted-foreground">{trend.label}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

